import React, { useState } from "react";
import {
  Table,
  Switch,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Tooltip,
  message,
  Popconfirm,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, BellOutlined } from "@ant-design/icons";
import { Header } from "../../../../components";
import {
  useFetchJobTypes,
  useFetchSystemJobs,
  useCreateSystemJob,
  useUpdateSystemJob,
  useDeleteSystemJob,
  useTestAlert,
} from "../hooks/useFetch";

// สี/ข้อความสถานะรอบล่าสุด
const STATUS_META = {
  ok: { color: "green", text: "ปกติ" },
  auth_expired: { color: "red", text: "Cookie หมดอายุ" },
  error: { color: "volcano", text: "ผิดพลาด" },
  idle: { color: "default", text: "ยังไม่รัน" },
};

function SystemJobPage() {
  const companyId = sessionStorage.getItem("activeCompanyId");
  const { data: jobs = [], isLoading, refetch } = useFetchSystemJobs(companyId);
  const { data: jobTypes = [] } = useFetchJobTypes();
  const { mutateAsync: createJob, isLoading: isCreating } = useCreateSystemJob();
  const { mutateAsync: updateJob } = useUpdateSystemJob();
  const { mutateAsync: deleteJob } = useDeleteSystemJob();
  const { mutateAsync: testAlert, isLoading: isTesting } = useTestAlert();

  const handleTestAlert = async () => {
    try {
      await testAlert();
      // ตัว toast จริงจะเด้งผ่าน WebSocket (ถ้า socket เชื่อมอยู่) — ข้อความนี้แค่ยืนยันว่าส่งคำสั่งแล้ว
      message.success("ส่งแจ้งเตือนทดสอบแล้ว — ควรเห็น toast เด้งภายในไม่กี่วินาที");
    } catch (e) {
      message.error(e?.response?.data?.message || "ส่งแจ้งเตือนทดสอบไม่สำเร็จ");
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = เพิ่มใหม่
  const [form] = Form.useForm();
  const [togglingId, setTogglingId] = useState(null);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      jobKey: record.job_key,
      name: record.name,
      url: record.config?.url || "",
      cookie: "", // ไม่ดึง cookie เดิมมาโชว์ (ปลอดภัย) — เว้นว่าง = คงของเดิม
    });
    setModalOpen(true);
  };

  // เปิด/ปิดเร็ว ๆ จากสวิตช์ในตาราง (ไม่ต้องเปิด modal)
  const handleToggle = async (record, checked) => {
    setTogglingId(record.id);
    try {
      await updateJob({ id: record.id, enabled: checked });
      message.success(checked ? "เปิดใช้งานแล้ว" : "ปิดใช้งานแล้ว");
      refetch();
    } catch (e) {
      message.error(e?.response?.data?.message || "เปลี่ยนสถานะไม่สำเร็จ");
    } finally {
      setTogglingId(null);
    }
  };

  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      // ส่ง cookie เฉพาะเมื่อกรอกใหม่ (เว้นว่าง = ไม่แตะของเดิมตอนแก้ไข)
      const config = { url: v.url };
      if (v.cookie && v.cookie.trim()) config.cookie = v.cookie.trim();

      if (editing) {
        await updateJob({ id: editing.id, name: v.name, config });
        message.success("บันทึกสำเร็จ");
      } else {
        await createJob({ jobKey: v.jobKey, name: v.name, enabled: false, config });
        message.success("เพิ่มระบบสำเร็จ (ยังปิดอยู่ — เปิดสวิตช์เมื่อพร้อม)");
      }
      setModalOpen(false);
      refetch();
    } catch (e) {
      if (e?.errorFields) return; // validation
      message.error(e?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteJob(record.id);
      message.success("ลบแล้ว");
      refetch();
    } catch (e) {
      message.error(e?.response?.data?.message || "ลบไม่สำเร็จ");
    }
  };

  const columns = [
    {
      title: "เปิด/ปิด",
      dataIndex: "enabled",
      width: 90,
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          loading={togglingId === record.id}
          onChange={(checked) => handleToggle(record, checked)}
        />
      ),
    },
    { title: "ชื่อ", dataIndex: "name" },
    { title: "ชนิด", dataIndex: "job_key", render: (k) => <Tag>{k}</Tag> },
    {
      title: "Cookie",
      dataIndex: "config",
      width: 130,
      render: (config) =>
        config?.cookieSet ? (
          <Tag color="green">ตั้งค่าแล้ว</Tag>
        ) : (
          <Tag color="red">ยังไม่ตั้ง</Tag>
        ),
    },
    {
      title: "สถานะล่าสุด",
      dataIndex: "last_status",
      render: (status, record) => {
        const meta = STATUS_META[status] || STATUS_META.idle;
        return (
          <Tooltip title={record.last_message || ""}>
            <Tag color={meta.color}>{meta.text}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "รันล่าสุด",
      dataIndex: "last_run_at",
      render: (t) => (t ? new Date(t).toLocaleString("th-TH") : "-"),
    },
    {
      title: "จัดการ",
      width: 130,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="ลบระบบนี้?" onConfirm={() => handleDelete(record)} okText="ลบ" cancelText="ยกเลิก">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="m-4 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Header category="Admin" title="ระบบเชื่อมต่ออัตโนมัติ" />
        <div style={{ display: "flex", gap: 8 }}>
          <Button icon={<BellOutlined />} loading={isTesting} onClick={handleTestAlert}>
            ทดสอบแจ้งเตือน
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            เพิ่มระบบ
          </Button>
        </div>
      </div>

      <p style={{ color: "#888", marginBottom: 16 }}>
        ตั้งค่าระบบที่ดึงข้อมูลอัตโนมัติเข้ามาสร้างเคส (รันทุก 1 นาที) · เปิด/ปิดได้ทันที ·
        เมื่อ Cookie หมดอายุ ให้กดแก้ไขแล้ววาง Cookie ใหม่
      </p>

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={jobs}
        columns={columns}
        pagination={false}
      />

      <Modal
        title={editing ? "แก้ไขระบบ" : "เพิ่มระบบ"}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="บันทึก"
        cancelText="ยกเลิก"
        confirmLoading={isCreating}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="jobKey" label="ชนิดระบบ" rules={[{ required: true, message: "เลือกชนิด" }]}>
            <Select disabled={!!editing} placeholder="เลือกชนิดระบบ">
              {jobTypes.map((t) => (
                <Select.Option key={t.key} value={t.key}>
                  {t.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="name" label="ชื่อเรียก" rules={[{ required: true, message: "กรอกชื่อ" }]}>
            <Input placeholder="เช่น MGB ถอน" />
          </Form.Item>
          <Form.Item name="url" label="URL หน้าเว็บ" rules={[{ required: true, message: "กรอก URL" }]}>
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="cookie"
            label={editing ? "Cookie (เว้นว่าง = ใช้ของเดิม)" : "Cookie"}
            rules={editing ? [] : [{ required: true, message: "วาง Cookie" }]}
          >
            <Input.TextArea rows={3} placeholder="วาง cookie จาก browser (F12 → Application → Cookies)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default SystemJobPage;
